import {StyleType} from '@wix/editor-platform-ooi-metadata-resolver'
import {ISiteTextPreset, IStyleParam, StyleParamType, createStylesParams, wixColorParam} from '@wix/tpa-settings'
import {GetDefaultValueProps, getDefaultFont} from '../../settings-commons/utils/style'

const getMembersDefaultV2Font = (
  params: GetDefaultValueProps,
  fontParam: IStyleParam<StyleParamType.Font>,
  mobileFontSize: number,
  preset: keyof ISiteTextPreset,
) => {
  const {isMobile, getStyleParamValue} = params
  const fontValue = getStyleParamValue(fontParam)

  if (isMobile) {
    const {value: _value, preset: _preset, ...rest} = fontValue
    return getDefaultFont(preset, {...rest, size: mobileFontSize})(params)
  }

  return fontValue
}

type IStyleParamsV1 = {
  pageTitleFont: StyleParamType.Font
  descriptionFont: StyleParamType.Font
  eventTextFont: StyleParamType.Font
  eventLinkFont: StyleParamType.Font
  eventLinkButtonFont: StyleParamType.Font
  noEventsTextFont: StyleParamType.Font
}

type IStyleParamsV2 = {
  pageTitleFontV2: StyleParamType.Font
  descriptionFontV2: StyleParamType.Font
  eventTextFontV2: StyleParamType.Font
  eventLinkFontV2: StyleParamType.Font
  eventLinkButtonFontV2: StyleParamType.Font
  noEventsTextFontV2: StyleParamType.Font
}

type IStyleParams = {
  selectedTabColor: StyleParamType.Color
  tabsColor: StyleParamType.Color
  tabsFont: StyleParamType.Font
  pageTitleColor: StyleParamType.Color
  descriptionColor: StyleParamType.Color
  eventTextColor: StyleParamType.Color
  eventLinkColor: StyleParamType.Color
  eventLinkButtonColor: StyleParamType.Color
  eventLinkButtonBorderColor: StyleParamType.Color
  eventLinkButtonBorderRadius: StyleParamType.Number
  eventLinkButtonBorderWidth: StyleParamType.Number
  noEventsTextColor: StyleParamType.Color
  noEventsButtonFont: StyleParamType.Font
  noEventsButtonColor: StyleParamType.Color
  noEventsButtonBorderRadius: StyleParamType.Number
  noEventsButtonBorderColor: StyleParamType.Color
  noEventsButtonBorderWidth: StyleParamType.Number
  backgroundColor: StyleParamType.Color
  backgroundBorderColor: StyleParamType.Color
  backgroundBorderWidth: StyleParamType.Number
  dividerColor: StyleParamType.Color
  dividerWidth: StyleParamType.Number
} & IStyleParamsV1 &
  IStyleParamsV2

const stylesParamsV1 = createStylesParams<IStyleParamsV1>({
  pageTitleFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Page-title',
        size: 28,
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Page-title', {
      size: 28,
    }),
  },
  descriptionFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Body-M', {
      size: 16,
    }),
  },
  eventTextFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Body-M', {
      size: 16,
    }),
  },
  eventLinkFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Body-M', {
      size: 16,
    }),
  },
  eventLinkButtonFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Body-M', {
      size: 16,
    }),
  },
  noEventsTextFont: {
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 24,
      },
    },
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Body-M', {
      size: 24,
    }),
  },
})

const stylesParamsV2 = createStylesParams<IStyleParamsV2>({
  pageTitleFontV2: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'pageTitleFont',
      },
    },
    getDefaultValue: params => getMembersDefaultV2Font(params, stylesParamsV1.pageTitleFont, 20, 'Page-title'),
  },
  descriptionFontV2: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'descriptionFont',
      },
    },
    getDefaultValue: params => getMembersDefaultV2Font(params, stylesParamsV1.descriptionFont, 16, 'Body-M'),
  },
  eventTextFontV2: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'eventTextFont',
      },
    },
    getDefaultValue: params => getMembersDefaultV2Font(params, stylesParamsV1.eventTextFont, 16, 'Body-M'),
  },
  eventLinkFontV2: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'eventLinkFont',
      },
    },
    getDefaultValue: params => getMembersDefaultV2Font(params, stylesParamsV1.eventLinkFont, 16, 'Body-M'),
  },
  eventLinkButtonFontV2: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'eventLinkButtonFont',
      },
    },
    getDefaultValue: params => getMembersDefaultV2Font(params, stylesParamsV1.eventLinkButtonFont, 16, 'Body-M'),
  },
  noEventsTextFontV2: {
    type: StyleParamType.Font,
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'ref',
        ref: 'noEventsTextFont',
      },
    },
    getDefaultValue: params => getMembersDefaultV2Font(params, stylesParamsV1.noEventsTextFont, 24, 'Body-M'),
  },
})

export default createStylesParams<IStyleParams>({
  ...stylesParamsV1,
  ...stylesParamsV2,
  selectedTabColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderBottomColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  tabsColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  tabsFont: {
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  pageTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  descriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  eventTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  eventLinkColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  eventLinkButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  eventLinkButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  eventLinkButtonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  eventLinkButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  noEventsTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  noEventsButtonFont: {
    type: StyleParamType.Font,
    getDefaultValue: getDefaultFont('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 16,
      },
    },
  },
  noEventsButtonColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  noEventsButtonBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  noEventsButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  noEventsButtonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  backgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 1),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 1,
      },
    },
  },
  backgroundBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.3,
      },
    },
  },
  backgroundBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.3,
      },
    },
  },
  dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
})
